import { Layers, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { SkeletonCard } from '../../../shared/components/Skeleton';
import { formatBRL } from '../../../shared/lib/format';
import {
  useCreateGrupo,
  useDeleteGrupo,
  useGrupos,
  useUpdateGrupo,
} from '../hooks';
import type { GrupoComplementos } from '../types';
import { GrupoFormModal } from './GrupoFormModal';

export function GruposTab() {
  const { data, isLoading } = useGrupos();
  const createMut = useCreateGrupo();
  const updateMut = useUpdateGrupo();
  const deleteMut = useDeleteGrupo();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<GrupoComplementos | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<GrupoComplementos | null>(null);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (g: GrupoComplementos) => {
    setEditing(g);
    setFormOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {data?.length ?? 0} grupo(s) cadastrado(s)
        </p>
        <button className="btn-primary" onClick={openCreate}>
          <Plus className="h-4 w-4" /> Novo grupo
        </button>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          icon={Layers}
          title="Nenhum grupo de complementos"
          description="Crie grupos como Frutas, Caldas e Recheios para personalizar pedidos."
          action={
            <button className="btn-primary" onClick={openCreate}>
              <Plus className="h-4 w-4" /> Criar grupo
            </button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {data!.map((g) => (
            <div key={g.id} className="card p-4">
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate font-semibold text-slate-900">{g.nome}</h3>
                  <p className="text-xs text-slate-500">
                    {g.obrigatorio ? 'Obrigatorio' : 'Opcional'} • min {g.min_selecao} / max{' '}
                    {g.max_selecao}
                    {!g.ativo && ' • Inativo'}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => openEdit(g)}
                    className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Editar"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(g)}
                    className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                    aria-label="Excluir"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {g.complementos.length === 0 ? (
                  <span className="text-xs text-slate-400">Sem itens</span>
                ) : (
                  g.complementos.slice(0, 8).map((c) => (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                    >
                      {c.nome}
                      {c.preco_adicional > 0 && (
                        <span className="text-slate-400">+{formatBRL(c.preco_adicional)}</span>
                      )}
                    </span>
                  ))
                )}
                {g.complementos.length > 8 && (
                  <span className="text-xs text-slate-400">
                    +{g.complementos.length - 8} mais
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <GrupoFormModal
        open={formOpen}
        grupo={editing}
        onClose={() => setFormOpen(false)}
        loading={createMut.isPending || updateMut.isPending}
        onSubmit={async (input) => {
          if (editing) {
            await updateMut.mutateAsync({ id: editing.id, body: input });
          } else {
            await createMut.mutateAsync(input);
          }
          setFormOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir grupo"
        message={`Deseja realmente excluir o grupo "${confirmDelete?.nome}"?`}
        variant="danger"
        loading={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (confirmDelete) {
            await deleteMut.mutateAsync(confirmDelete.id);
            setConfirmDelete(null);
          }
        }}
      />
    </div>
  );
}
